import { isOurs } from './selector.js';

/**
 * A flat view of the page's visible text, plus the map back into DOM positions.
 *
 * Text anchors are the durable ones: a quote survives its element being renamed,
 * re-nested or restyled. But finding a quote means searching the document as one
 * string, and turning a hit back into a Range means knowing which text node each
 * character came from. That is all this is.
 */
export interface TextIndex {
  text: string;
  chunks: { node: Text; start: number }[];
}

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'TITLE']);

export function buildTextIndex(): TextIndex {
  const chunks: { node: Text; start: number }[] = [];
  let text = '';

  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
      if (isOurs(parent)) return NodeFilter.FILTER_REJECT;
      if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let node = walker.nextNode() as Text | null;
  while (node) {
    chunks.push({ node, start: text.length });
    text += node.nodeValue ?? '';
    node = walker.nextNode() as Text | null;
  }

  return { text, chunks };
}

/** Where a (node, offset) DOM position lands in the flat string. */
export function globalOffset(index: TextIndex, node: Node, offset: number): number | null {
  if (!(node instanceof Text)) {
    // A boundary on an element resolves to the start of its first indexed
    // descendant, which is what a selection landing on an element edge means.
    const first = firstTextIn(node);
    if (!first) return null;
    const chunk = index.chunks.find((c) => c.node === first);
    return chunk ? chunk.start : null;
  }
  const chunk = index.chunks.find((c) => c.node === node);
  return chunk ? chunk.start + offset : null;
}

function firstTextIn(node: Node): Text | null {
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
  return walker.nextNode() as Text | null;
}

/** Turn a span of the flat string back into a live Range. */
export function rangeFromGlobal(index: TextIndex, start: number, end: number): Range | null {
  const startPos = domPosition(index, start);
  const endPos = domPosition(index, end);
  if (!startPos || !endPos) return null;

  const range = document.createRange();
  try {
    range.setStart(startPos.node, startPos.offset);
    range.setEnd(endPos.node, endPos.offset);
  } catch {
    return null;
  }
  return range;
}

function domPosition(index: TextIndex, global: number): { node: Text; offset: number } | null {
  for (let i = index.chunks.length - 1; i >= 0; i--) {
    const chunk = index.chunks[i]!;
    if (global >= chunk.start) {
      const len = chunk.node.nodeValue?.length ?? 0;
      return { node: chunk.node, offset: Math.min(global - chunk.start, len) };
    }
  }
  return null;
}

export interface QuoteMatch {
  start: number;
  end: number;
  score: number;
}

/**
 * Find a quote in the page, using its surrounding text to choose between
 * repeats.
 *
 * "Learn more" might appear nine times; the prefix and suffix are what say which
 * one someone commented on. When two candidates score the same we return
 * nothing, because picking one at random is how a comment ends up attached to
 * the wrong paragraph while looking perfectly confident.
 */
export function findQuote(
  index: TextIndex,
  quote: string,
  prefix: string,
  suffix: string,
): QuoteMatch | null {
  if (!quote) return null;

  const matches: QuoteMatch[] = [];
  let from = 0;
  for (;;) {
    const at = index.text.indexOf(quote, from);
    if (at === -1) break;
    matches.push({
      start: at,
      end: at + quote.length,
      score: contextScore(index.text, at, at + quote.length, prefix, suffix),
    });
    from = at + 1;
    if (matches.length > 200) break;
  }

  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0]!;

  matches.sort((a, b) => b.score - a.score);
  const best = matches[0]!;
  const runnerUp = matches[1]!;
  if (best.score === runnerUp.score) return null;
  return best;
}

/** How many characters of the stored prefix/suffix still line up, counted
 *  inward from the quote. */
function contextScore(
  text: string,
  start: number,
  end: number,
  prefix: string,
  suffix: string,
): number {
  let score = 0;

  const actualPrefix = text.slice(Math.max(0, start - prefix.length), start);
  for (let i = 1; i <= Math.min(prefix.length, actualPrefix.length); i++) {
    if (prefix[prefix.length - i] === actualPrefix[actualPrefix.length - i]) score++;
    else break;
  }

  const actualSuffix = text.slice(end, end + suffix.length);
  for (let i = 0; i < Math.min(suffix.length, actualSuffix.length); i++) {
    if (suffix[i] === actualSuffix[i]) score++;
    else break;
  }

  return score;
}
